import { Status } from '@df/core/status';
import { NICmsDataScope } from '@icms/core/data/scope/icms-data-scope.service';
import { NICmsContentComponent } from '..';

export namespace NICmsContentField {
    /**
     * Type of field in admin form
     */
    export enum EFieldType {
        text,
        textExtended,
        textSelect,
        number,
        choice,
        checkbox,
        repeater,
        components,
        image,
        file,
        textarea,
        outlet,
        label,
        relation,
        button,
        toggle,
        date,
        slider,
        colorPicker,
        audio,
        document,
        radio,
        time,
        datetime,
        object
    }

    export type TOptionFieldPreviewMode = 'color' | 'image' | 'icon';

    /**
     * Field metadata
     */
    export interface IField<TField = any, TContainer = any> {
        /**
         * Data key
         */
        key: string;
        /**
         * Field type
         */
        type: EFieldType;
        /**
         * Field title shown in form
         */
        title: string;
        /**
         * Field placeholder for user
         */
        placeholder?: string;
        /**
         * Field hint for user
         */
        description?: string;
        /**
         * Field helper for user
         */
        helper?: string;
        /**
         * Tab under which the field is shown in iCMS editor
         * (defaults to 'Configuration')
         */
        group?: string;
        /**
         * Default value used as default when component is created
         */
        default?: TField; //  | ((page: IPage<ICmsSectionPageModel>) => any);
        /**
         * If field value is required
         * falsy values are fine
         */
        required?: boolean | ((data: TContainer) => boolean | null | undefined);
        /**
         * ACL
         * true - is available for any user to change
         * false - is only available when cms_components ACL permission is granted
         */
        protected?: boolean;
        /**
         * Optional test when field can be usd
         * falsy values are fine
         */
        canShow?: (data: TContainer, outletOptions: NICmsContentComponent.IOutletOptions) => boolean | undefined | null;
        /**
         * Optional validator function returning sanitize value or throw `Error`
         *
         * @param value {TField} value
         */
        sanitize?: (value: TField) => TField | Error | { error: string; value: TField } | undefined;
        /**
         * Form display style
         */
        display?: 'mui' | 'default';
        /**
         * Field preparation method
         * in case it needs to load any data
         */
        prepare?: () => Promise<any>;
        /**
         * Field can be edit in mirror child
         */
        mirrorEditable?: boolean;
        /**
         * On field change callback
         */
        onChange?: TFieldEventMethod;
        /**
         * On field focus callback
         */
        onFocus?: TFieldEventMethod;
    }

    /**
     * Special field for text with options
     */
    export interface ITextSelectField<T = any, TField = any, TContainer = any> extends IField<TField, TContainer> {
        type: EFieldType.textSelect;
        options: IOption<T>[] | IOptionFn<T, TField>;
        validateFn?: (value: string | undefined) => boolean;
    }

    /**
     * Special field for choice fields
     */
    export interface IChoiceField<T = any, TField = any, TContainer = any> extends IField<TField, TContainer> {
        type: EFieldType.choice;
        options: IOption<T>[] | IOptionFn<T, TField>;
        multiple?: boolean;
        previewMode?: TOptionFieldPreviewMode;
    }

    /**
     * Special field for number type of fields
     */
    export interface INumberField<TField = any, TContainer = any> extends IField<TField, TContainer> {
        type: EFieldType.number;
        min?: number;
        max?: number;
        step?: number;
    }

    /**
     * Special field for textarea
     */
    export interface ITextField<TField = any, TContainer = any> extends IField<TField, TContainer> {
        type: EFieldType.text | EFieldType.textarea;
        maxLength?: number | number[];
    }

    /**
     * Special field for exteded text field
     */
    export interface ITextFieldExtended<T = any, TField = any, TContainer = any> extends IField<TField, TContainer> {
        type: EFieldType.textExtended;
        selectTags?: boolean;
        selectColor?: boolean;
        colorOptions?: IOption<T>[] | IOptionFn<T, TField>;
        colorPreview?: boolean;
        textArea?: boolean;
    }

    export interface IOption<T = any> {
        key: T;
        title?: string;
        value?: T;
    }

    export interface ISliderField<TField = any, TContainer = any> extends IField<TField, TContainer> {
        type: EFieldType.slider;
        step?: number;
        datalist?: boolean | ((field: ISliderField) => { [key: number]: string });
        min?: number;
        max?: number;
    }

    export interface IButtonField<TField = any, TContainer = any> extends IField<TField, TContainer> {
        type: EFieldType.button;
        icon?: string;
        onClick: (status: Status) => void;
    }

    export interface IDateField<TField = any, TContainer = any> extends IField<TField, TContainer> {
        type: EFieldType.date;
        min?: string;
        max?: string;
    }

    export interface ITimeField<TField = any, TContainer = any> extends IField<TField, TContainer> {
        type: EFieldType.time;
        min?: string;
        max?: string;
    }

    export interface IDatetimeField<TField = any, TContainer = any> extends IField<TField, TContainer> {
        type: EFieldType.datetime;
        minTime?: string;
        maxTime?: string;
        minDate?: string;
        maxDate?: string;
    }

    export interface IToggleField<TContainer = any> extends IField<boolean, TContainer> {
        type: EFieldType.toggle;
        invert?: boolean;
        labels?: { true?: string; false?: string };
    }

    export interface IColorPickerField<TContainer = any> extends IField<string, TContainer> {
        type: EFieldType.colorPicker;
    }

    export type IOptionFn<OptionType = any, ModelType = any> = ((data: ModelType) => IOption<OptionType>[]) | (() => IOption<OptionType>[]);

    export interface IRelationField<T = any, M = any> extends IField<T> {
        options: IRelationOptions<T, M> | ((data: M) => IRelationOptions<T, M>);
    }

    /**
     * Special field for radio input
     */
    export interface IRadioField<T = any, TField = any, TContainer = any> extends IField<TField, TContainer> {
        options: IOption<T>[] | IOptionFn<T, TField>;
    }
    // export type ISelectionRequestFn<T = any> = ((data: T) => ISelectionRequest) | (() => ISelectionRequest);

    export interface IRelationOptions<T, M> {
        // model window title
        title: string;
        // resource type
        type: string;
        /**
         * If multi selection is allowed
         * (= model is array, otherwise limit is ignored)
         */
        multiple?: boolean;
        // selection limit
        limit?: number;
        // id selection order matters
        ordered?: boolean;
        // filter to apply on BE
        loadFilter?: any;
        // filter to apply on FE
        filter?: (model: M, data?: T) => boolean;
        // can add new resource
        addNew?: boolean;
        onBeforeSave?: (model: M) => void;
    }

    export interface IAbstractRepeaterField<TItem = any, TContainer = any> extends IField<TItem[], TContainer> {
        // you can pass default size to auto generate that number of rows
        defaultSize?: number;
        // initial data (initial part of default)
        defaultPart?: Partial<TItem>;
        // item name to use when listing items
        itemName: string;
        // you can pass field id here, value of that field will be used to represent item on list (to make sorting easier)
        itemNameField?: string;
        minSize?: number;
        maxSize?: ((data: TContainer) => number) | number;
        // repeater fields in new tray
        complex?: boolean;
        // method called before adding the row, if false value is returned regular processing will not be continued
        // onBeforeAdd?: (row: Partial<T>, component: CmsFormFieldAbstractRepeaterComponent<any, IAbstractRepeaterField>) => boolean;
        // onBeforeAdd?: (row: Partial<T>, component) => boolean;
        // if row can be displayed
        // canShowRow?: (row: Partial<T>) => boolean;
    }

    export interface IRepeaterField<TItem = any, TContainer = any> extends IAbstractRepeaterField<TItem, TContainer> {
        fields: IField<TItem, TContainer>[];
    }

    export interface IComponentsField<TItem = any, TContainer = any> extends IAbstractRepeaterField<TItem, TContainer> {}

    export type TFieldEventMethod = (event: IFieldEvent) => IFieldEventResult | undefined;

    export interface IFieldEvent<TValue = any, TContainer = any> {
        field: IField<TValue, TContainer>;
        value: TValue;
        scopeData: TContainer;
    }

    export interface IObjectField<TObjectField = any, TContainer = any> extends IField<TObjectField, TContainer> {
        type: EFieldType.object;
        fields: IField<any, TObjectField>[];
    }

    export interface IFieldEventResult<TContainer = any> {
        /**
         * Changes to apply
         */
        changes: NICmsDataScope.IChangeRequest<TContainer, any>[];
    }
}
